import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { ButtonAction } from "../../../components/NewButtons";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { StyledForm } from "../../../components/StyledForm";
import { DatePickerInput } from "../../../components/forms/inputs/DatePickerInput";
import { css } from "@emotion/react";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (fromDate: string, toDate: string) => Promise<void>;
  monthYearPicker?: boolean;
};

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const CustomReportingPeriodModal = ({
  isOpen,
  onClose,
  onSubmit,
  monthYearPicker = false,
}: Props) => {
  const isSuperAdmin = useSelector(authSelectors.isSuperAdmin);

  const validationSchema = yup.object({
    fromDate: yup
      .string()
      .required(
        monthYearPicker ? "Month is required" : "From date is required"
      ),
    toDate: yup
      .string()
      .test("Testing if toDate is required", "To date is required", (value) => {
        if (monthYearPicker) {
          return true;
        }
        return !!value;
      }),
  });

  const formMethods = useForm<{ fromDate: string; toDate: string }>({
    defaultValues: {
      fromDate: "",
      toDate: "",
    },
    resolver: yupResolver(validationSchema),
  });

  return (
    <FormProvider {...formMethods}>
      <ConfirmActionModal
        isOpen={isOpen}
        onClose={() => {
          formMethods.reset();
          onClose();
        }}
        title="Choose period for export"
        confirmButton={
          <ButtonAction
            onClick={formMethods.handleSubmit(async (data) => {
              await onSubmit(data.fromDate, data.toDate);
            })}
            type="button"
            disabled={formMethods.formState.isSubmitting}
          >
            {formMethods.formState.isSubmitting ? <ActionSpinner /> : "Export"}
          </ButtonAction>
        }
      >
        <StyledForm additionalStyles={formStyle}>
          <DatePickerInput
            name="fromDate"
            label={monthYearPicker ? "Month" : "From date"}
            placeholder={monthYearPicker ? "Select month" : "Select from date"}
            width="100%"
            minDate={isSuperAdmin ? new Date("2025-01-01") : undefined}
            maxDate={new Date(formMethods.watch("toDate"))}
            showMonthYearPicker={monthYearPicker}
          />
          {!monthYearPicker && (
            <DatePickerInput
              name="toDate"
              label="To date"
              placeholder="Select to date"
              width="100%"
              minDate={new Date(formMethods.watch("fromDate"))}
            />
          )}
        </StyledForm>
      </ConfirmActionModal>
    </FormProvider>
  );
};
